/**
 * Parser for anime watch history data
 * Handles the format from 元データ.txt
 */

/**
 * プラットフォームをURLから判定
 */
export function detectPlatform(url) {
  if (!url) return null

  const lowerUrl = url.toLowerCase()

  if (lowerUrl.includes('amazon.co.jp') || lowerUrl.includes('primevideo.com')) {
    return 'prime'
  }
  if (lowerUrl.includes('netflix.com')) {
    return 'netflix'
  }
  if (lowerUrl.includes('unext.jp') || lowerUrl.includes('video.unext')) {
    return 'unext'
  }
  if (lowerUrl.includes('crunchyroll.com')) {
    return 'crunchyroll'
  }
  if (lowerUrl.includes('danimemall') || lowerUrl.includes('danime')) {
    return 'danime'
  }
  if (lowerUrl.includes('abema.tv')) {
    return 'abema'
  }
  if (lowerUrl.includes('hulu.jp')) {
    return 'hulu'
  }

  return 'other'
}

/**
 * ステータス行から視聴状況を解析
 */
export function parseStatus(statusLine) {
  if (!statusLine) {
    return { currentEpisode: 0, status: 'plan_to_watch', season: null }
  }

  const line = statusLine.trim()

  // 未スタート系
  if (line.includes('未スタート') || line.includes('未だスタート')) {
    // シーズン情報があるかチェック
    const seasonMatch = line.match(/シーズン\s*(\d+)/i) || line.match(/第\s*(\d+)\s*期/i)
    return {
      currentEpisode: 0,
      status: 'plan_to_watch',
      season: seasonMatch ? `シーズン${seasonMatch[1]}` : null
    }
  }

  // X話まで / Xwまで (wは週の略)
  const episodeMatch = line.match(/(\d+)\s*話まで/i) || line.match(/(\d+)\s*wまで/i)
  if (episodeMatch) {
    const currentEpisode = parseInt(episodeMatch[1], 10)

    // シーズン情報もチェック
    const seasonMatch = line.match(/シーズン\s*(\d+)/i) || line.match(/第\s*(\d+)\s*期/i)

    return {
      currentEpisode,
      status: 'watching',
      season: seasonMatch ? `シーズン${seasonMatch[1]}` : null
    }
  }

  // 完了系
  if (line.includes('完了') || line.includes('完走') || line.includes('視聴完了')) {
    return { currentEpisode: -1, status: 'completed', season: null }
  }

  // デフォルト
  return { currentEpisode: 0, status: 'plan_to_watch', season: null }
}

/**
 * 生データをパースしてアニメエントリの配列を返す
 */
export function parseRawData(text) {
  if (!text || typeof text !== 'string') {
    return []
  }

  const lines = text.split('\n')
  const entries = []
  let currentEntry = null
  let lineIndex = 0

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim()

    // 空行の場合、現在のエントリを保存してリセット
    if (!line) {
      if (currentEntry && currentEntry.title) {
        entries.push(finalizeEntry(currentEntry))
      }
      currentEntry = null
      lineIndex = 0
      continue
    }

    // 新しいエントリ開始
    if (!currentEntry) {
      currentEntry = {
        id: generateId(),
        title: '',
        url: '',
        currentEpisode: 0,
        totalEpisodes: null,
        status: 'plan_to_watch',
        platform: null,
        season: null,
        daveScore: 0,
        daveScoreDetail: {
          axis_story: 0,
          axis_visual: 0,
          axis_excitement: 0,
          axis_emotion: 0,
          axis_fluency: 0
        },
        notes: '',
        metadata: null,
        lastUpdated: new Date().toISOString()
      }
    }

    // 行の役割を判定
    if (lineIndex === 0) {
      // 1行目: タイトル
      currentEntry.title = line
    } else if (lineIndex === 1) {
      // 2行目: URL
      if (line.startsWith('http')) {
        currentEntry.url = line
        currentEntry.platform = detectPlatform(line)
      } else {
        // URLでない場合はステータスとして扱う
        const statusInfo = parseStatus(line)
        Object.assign(currentEntry, statusInfo)
        lineIndex++ // 次の行はスキップ
      }
    } else if (lineIndex === 2) {
      // 3行目: ステータス
      const statusInfo = parseStatus(line)
      Object.assign(currentEntry, statusInfo)
    }

    lineIndex++
  }

  // 最後のエントリを追加
  if (currentEntry && currentEntry.title) {
    entries.push(finalizeEntry(currentEntry))
  }

  return entries
}

/**
 * エントリを完成させる（型チェック、デフォルト値設定）
 */
function finalizeEntry(entry) {
  return {
    ...entry,
    type: 'series',
    format: 'anime',
    location: 'streaming',
    airingStatus: null,
    broadcastDay: null,
    nextEpisodeDate: null,
    dropReason: null,
    eventDate: null,
    seriesId: null,
    history: []
  }
}

/**
 * ユニークIDを生成
 */
function generateId() {
  return `anime_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`
}

/**
 * プラットフォーム名を取得
 */
export function getPlatformName(platform) {
  const names = {
    prime: 'Prime Video',
    netflix: 'Netflix',
    unext: 'U-NEXT',
    crunchyroll: 'Crunchyroll',
    danime: 'd Anime Store',
    abema: 'ABEMA',
    hulu: 'Hulu',
    other: 'Other'
  }
  return names[platform] || platform
}

/**
 * プラットフォームの略称を取得
 */
export function getPlatformAbbr(platform) {
  const abbrs = {
    prime: 'P',
    netflix: 'N',
    unext: 'U',
    crunchyroll: 'C',
    danime: 'D',
    abema: 'A',
    hulu: 'H',
    other: '?'
  }
  return abbrs[platform] || '?'
}

export default {
  parseRawData,
  parseStatus,
  detectPlatform,
  getPlatformName,
  getPlatformAbbr
}
